// src/utils/mergeChanges.ts

import { type ProductImage, type ChangeSummary, type ImageChange, type ProductVariant } from '../types';

export const mergeProductVariantsWithChanges = (
    currentVariants: ProductVariant[],
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean,
    forceApply: boolean = false
): ProductVariant[] => {
    if (!(isReviewMode || forceApply) || !changeSummary?.variants) {
        return currentVariants;
    }

    const variantChanges = changeSummary.variants;
    let merged = [...currentVariants];


    // ✅ Mark removed variants instead of filtering them out
    const removedIds = new Set(variantChanges.removed?.map(v => v.id) || []);
    merged = merged.map(v => {
        if (removedIds.has(v.id)) {
            return { ...v, _action: 'removed' };
        }
        return v;
    });

    // Process added variants
    const addedVariants = variantChanges.added || [];
    addedVariants.forEach(variantData => {
        merged.push({
            id: variantData.temp_id || Date.now() * -1,
            name: variantData.name || '',
            sku_suffix: variantData.sku_suffix || '',
            is_active: variantData.is_active ?? true,
            price: variantData.price ?? null,
            cost_price: variantData.cost_price ?? null,
            description: variantData.description || '',
            specs: variantData.specs || [],
            use_product_images: variantData.use_product_images ?? false,
            _action: 'added'
        });
    });

    // Process changed variants
    const changedVariants = variantChanges.changed || [];
    changedVariants.forEach(change => {
        const existing = merged.find(v => v.id === change.id);
        if (existing) {
            const changedFields = change.changed_fields || {};
            // Apply changes
            Object.keys(changedFields).forEach(field => {
                if (field === 'specs') {
                    // Specs need special handling
                    existing.specs = changedFields[field].new || existing.specs;
                    existing._changed_fields = changedFields;
                } else {
                    existing[field] = changedFields[field].new;
                }
            });
            existing._action = 'changed';
            existing._changed_fields = changedFields;
        }
    });

    // Add _action for existing (unchanged) variants
    merged.forEach(v => {
        if (!v._action) {
            v._action = 'existing';
        }
    });

    return merged;
};

// export const mergeProductVariantsWithChanges = (
//     currentVariants: ProductVariant[],
//     changeSummary: ChangeSummary | null,
//     isReviewMode: boolean
// ): ProductVariant[] => {
//     if (!isReviewMode || !changeSummary?.variants?.items) {
//         return currentVariants;
//     }

//     const changedItems = changeSummary.variants.items;

//     // Create a map of changes by ID/temp_id for quick lookup
//     const changesMap = new Map();
//     changedItems.forEach(item => {
//         if (item.id) {
//             changesMap.set(item.id, item);
//         }
//         if (item.temp_id) {
//             changesMap.set(item.temp_id, item);
//         }
//     });

//     // Process current variants
//     let merged = currentVariants.map(variant => {
//         const change = changesMap.get(variant.id);
//         if (change) {
//             if (change.action === 'removed') {
//                 return {
//                     ...variant,
//                     _action: 'removed'
//                 };
//             }
//             // Apply changes
//             const updated = { ...variant };
//             if (change.changed_fields) {
//                 Object.keys(change.changed_fields).forEach(field => {
//                     if (field === 'specs') {
//                         updated.specs = change.changed_fields[field].new;
//                     } else {
//                         updated[field] = change.changed_fields[field].new;
//                     }
//                 });
//             }
//             return {
//                 ...updated,
//                 _action: change.action,
//                 _changed_fields: change.changed_fields  // ✅ Store changed_fields for UI
//             };
//         }
//         return {
//             ...variant,
//             _action: 'existing'
//         };
//     });

//     // Add new variants
//     changedItems.forEach(item => {
//         if (item.action === 'added' && item.new) {
//             const newVariant: ProductVariant = {
//                 id: item.temp_id || Date.now() * -1,
//                 name: item.new.name || '',
//                 sku_suffix: item.new.sku_suffix || '',
//                 is_active: item.new.is_active ?? true,
//                 price: item.new.price ?? null,
//                 cost_price: item.new.cost_price ?? null,
//                 description: item.new.description || '',
//                 specs: item.new.specs || [],
//                 _action: 'added'
//             };
//             merged.push(newVariant);
//         }
//     });

//     return merged;
// };

/**
 * Merges current product images with pending changes from changeSummary
 * Returns a combined array ready for display/editing
 */
export const mergeProductImagesWithChanges = (
    currentImages: ProductImage[],
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean,
    forceApply: boolean = false
): ProductImage[] => {
    if (!(isReviewMode || forceApply) || !changeSummary?.images?.items) {
        return currentImages;
    }

    const changedItems = changeSummary.images.items;

    // Start with current images
    let merged = [...currentImages];

    // Apply changes and additions
    changedItems.forEach(item => {
        if (item.action === 'added') {
            // Determine the variant ID (use temp_variant_ref if available, otherwise product_variant)
            const variantId = item.temp_variant_ref || item.product_variant || null;
            // Add new image
            merged.push({
                id: Date.now() * -1,
                image_url: item.image_url,
                alt_text: item.alt_text || '',
                is_primary: item.is_primary || false,
                position: item.position || 0,
                product_variant: variantId,
                _action: 'added'
            });
        } else if (item.action === 'removed') {
            // Find existing image and mark as removed
            const existing = merged.find(img => img.id === item.id);
            if (existing) {
                existing._action = 'removed';
                // Keep the old data for display
            }
        } else if (item.changes) {
            // Changed image - find and update
            const existing = merged.find(img => img.id === item.id);
            if (existing) {
                if (item.changes.is_primary) existing.is_primary = item.changes.is_primary.new;
                if (item.changes.position) existing.position = item.changes.position.new;
                if (item.changes.alt_text) existing.alt_text = item.changes.alt_text.new;
                existing._action = item.action;
            }
        }
    });

    // Sort by position
    return merged.sort((a, b) => (a.position || 0) - (b.position || 0));
};
/**
 * Gets the change details for a specific image
 */
export const getImageChanges = (
    image: ProductImage,
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean
): { [field: string]: { old: any; new: any } } | null => {
    if (!isReviewMode || !changeSummary?.images) return null;

    const imageItem = changeSummary.images.items?.find(
        item => item.id === image.id || item.image_url === image.image_url
    );

    return imageItem?.changes || null;
};

/**
 * Gets the old value of a specific field for an image
 */
export const getImageFieldChange = (
    image: ProductImage,
    fieldName: string,
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean
): { value: any; previous: any; hasChanged: boolean } => {
    const changes = getImageChanges(image, changeSummary, isReviewMode);

    if (changes && changes[fieldName]) {
        return {
            value: changes[fieldName].new,
            previous: changes[fieldName].old,
            hasChanged: true
        };
    }

    // Fallback: use current value
    return {
        value: image[fieldName],
        previous: undefined,
        hasChanged: false
    };
};

// src/utils/mergeChanges.ts

/**
 * Gets a human-readable description of image changes
 */
export const getImageChangeDescription = (
    image: ProductImage,
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean
): string | null => {
    if (!isReviewMode || !changeSummary?.images) return null;

    const imageItem = changeSummary.images.items?.find(
        item => item.id === image.id || item.image_url === image.image_url
    );

    if (!imageItem) return null;

    if (imageItem.action === 'added') return 'Image added';
    if (imageItem.action === 'removed') return 'Image removed';
    if (imageItem.action === 'variant_moved') {
        return imageItem.change_summary || 'Image moved to different variant';
    }

    if (imageItem.action === 'changed' && imageItem.changes) {
        const changeDescriptions: string[] = [];
        const changes = imageItem.changes;

        if (changes.is_primary) {
            changeDescriptions.push(changes.is_primary.new ? 'Set as primary' : 'Removed as primary');
        }
        if (changes.position) {
            changeDescriptions.push('Order changed');
        }
        if (changes.alt_text) {
            changeDescriptions.push('Alt text updated');
        }
        if (changes.product_variant) {
            const variantChange = changes.product_variant;
            changeDescriptions.push(`Moved from ${variantChange.old_name} to ${variantChange.new_name}`);
        }

        return changeDescriptions.join(', ');
    }

    return null;
};

/**
 * Gets the status of an image (added, removed, changed, unchanged, variant_moved)
 */
export const getImageStatusWithChanges = (
    image: ProductImage,
    changeSummary: ChangeSummary | null,
    isReviewMode: boolean
): 'added' | 'removed' | 'changed' | 'variant_moved' | 'existing' => {
    if (!isReviewMode || !changeSummary?.images) return 'existing';

    const { images } = changeSummary;

    // Check if removed
    if (images.removed?.some(removed => removed.id === image.id)) {
        return 'removed';
    }

    // Check in items array
    const imageItem = images.items?.find(item => item.id === image.id || item.image_url === image.image_url);

    if (!imageItem) return 'existing';

    // Check action
    if (imageItem.action === 'added') return 'added';
    if (imageItem.action === 'changed') return 'changed';
    if (imageItem.action === 'variant_moved') return 'variant_moved';

    return 'existing';
};